/**
 * Shared UI primitives for loading, error, and empty states.
 *
 * Skeleton    — animated placeholder while content loads
 * ErrorState  — friendly error with retry button
 * EmptyState  — zero-data state with call to action
 * LoadingSpinner — simple centered spinner
 * PageLoader  — full-page loading with skeleton layout
 */

import React from 'react';

// ─── Skeleton ─────────────────────────────────────────────────────────────

const shimmer = `
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const skeletonStyle = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '800px 100%',
  animation: 'shimmer 1.4s infinite linear',
  borderRadius: 6,
};

export function Skeleton({ width = '100%', height = 16, style = {} }) {
  return (
    <>
      <style>{shimmer}</style>
      <div style={{ ...skeletonStyle, width, height, ...style }} />
    </>
  );
}

// ─── Skeleton layouts ─────────────────────────────────────────────────────

export function SkeletonCard({ rows = 3 }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '0.5px solid #e5e7eb' }}>
      <Skeleton width="40%" height={18} style={{ marginBottom: 16 }} />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} width={`${70 + (i % 3) * 10}%`} height={13} style={{ marginBottom: 10 }} />
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 4 }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '0.5px solid #e5e7eb' }}>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, padding: '12px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} width="60%" height={11} />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, padding: '12px 16px', borderBottom: '1px solid #f9fafb', background: r % 2 === 0 ? '#fff' : '#fafafa' }}>
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} width={`${50 + ((r + c) % 4) * 12}%`} height={13} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonKPIRow({ count = 4 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`, gap: 12, marginBottom: 20 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', border: '0.5px solid #e5e7eb' }}>
          <Skeleton width="55%" height={11} style={{ marginBottom: 10 }} />
          <Skeleton width="70%" height={24} style={{ marginBottom: 6 }} />
          <Skeleton width="40%" height={11} />
        </div>
      ))}
    </div>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────

export function ErrorState({ message, onRetry, compact = false }) {
  if (compact) {
    return (
      <div style={{ padding: '16px 20px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span style={{ fontSize: 13, color: '#991b1b' }}>{message || 'Failed to load data'}</span>
        </div>
        {onRetry && (
          <button onClick={onRetry} style={{ padding: '5px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '48px 24px', textAlign: 'center', background: '#fff', borderRadius: 12, border: '0.5px solid #e5e7eb' }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>⚠️</div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
        Failed to load
      </h3>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.5 }}>
        {message || 'Something went wrong loading this data. Please try again.'}
      </p>
      {onRetry && (
        <button onClick={onRetry} style={{ padding: '9px 20px', background: '#1a2535', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Try again
        </button>
      )}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────

export function EmptyState({ icon = '📋', title, description, action, onAction }) {
  return (
    <div style={{ padding: '52px 24px', textAlign: 'center', background: '#fff', borderRadius: 12, border: '0.5px solid #e5e7eb' }}>
      <div style={{ fontSize: 44, marginBottom: 14 }}>{icon}</div>
      {title && <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>{title}</h3>}
      {description && <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 20px', lineHeight: 1.5, maxWidth: 340, marginLeft: 'auto', marginRight: 'auto' }}>{description}</p>}
      {action && onAction && (
        <button onClick={onAction} style={{ padding: '9px 20px', background: '#1a2535', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {action}
        </button>
      )}
    </div>
  );
}

// ─── Loading spinner ──────────────────────────────────────────────────────

export function LoadingSpinner({ size = 36, message = '' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: 14 }}>
      <div style={{
        width: size, height: size,
        border: `3px solid #e5e7eb`,
        borderTop: `3px solid #1a2535`,
        borderRadius: '50%',
        animation: 'spin 0.75s linear infinite',
      }} />
      {message && <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>{message}</p>}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Inline error message ─────────────────────────────────────────────────

export function InlineError({ message, style = {} }) {
  if (!message) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7, padding: '10px 13px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#dc2626', lineHeight: 1.4, ...style }}>
      <span style={{ flexShrink: 0 }}>⚠</span>
      <span>{message}</span>
    </div>
  );
}

// ─── Network offline banner ───────────────────────────────────────────────

export function OfflineBanner() {
  const [offline, setOffline] = React.useState(!navigator.onLine);

  React.useEffect(() => {
    const on  = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  if (!offline) return null;

  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 100, background: '#1a2535', color: '#fff', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
      <span>📡</span>
      <span>You're offline. Some data may not be up to date. Changes will sync when you reconnect.</span>
    </div>
  );
}
